<script setup lang="ts">
const props = withDefaults(defineProps<{
  pairingCode?: string
  pairingUrl?: string
  peerPresentersCount?: number
  peerReady?: boolean
  popupPresenterConnected?: boolean
  presentationConnecting?: boolean
  presentationSupported?: boolean
  presenterEntryUrl?: string
}>(), {
  pairingCode: '',
  pairingUrl: '',
  peerPresentersCount: 0,
  peerReady: false,
  popupPresenterConnected: false,
  presentationConnecting: false,
  presentationSupported: true,
  presenterEntryUrl: '',
})

const emit = defineEmits<{
  connectPresentationWindow: []
  disconnectPeerPresenters: []
  disconnectPresentation: []
  launchDisplayMode: []
  regeneratePairingCode: []
}>()

const { data: ui } = useGamePresentationUI()

const presentationTabs = computed(() => [
  {
    icon: 'mdi:monitor',
    label: ui.value?.tabThisDevice,
    slot: 'this-device' as const,
    value: 'this-device',
  },
  {
    icon: 'mdi:cellphone-link',
    label: ui.value?.tabRemoteDisplay,
    slot: 'remote-display' as const,
    value: 'remote-display',
  },
  {
    icon: 'mdi:television-play',
    label: ui.value?.tabUseAsDisplay,
    slot: 'use-as-display' as const,
    value: 'use-as-display',
  },
])

// ── Connection status pills (consistent across tabs) ───────────
type StatusPill = { icon: string, label: string, tone: StatusTone }
type StatusTone = 'connected' | 'connecting' | 'error' | 'idle'

const popupStatus = computed<StatusPill>(() => {
  if (!props.presentationSupported) {
    return { icon: 'mdi:alert-circle-outline', label: ui.value?.pillUnsupported ?? '', tone: 'error' }
  }
  if (props.popupPresenterConnected) {
    return { icon: 'mdi:check-circle', label: ui.value?.pillConnected ?? '', tone: 'connected' }
  }
  if (props.presentationConnecting) {
    return { icon: 'mdi:progress-clock', label: ui.value?.pillConnecting ?? '', tone: 'connecting' }
  }
  return { icon: 'mdi:circle-outline', label: ui.value?.pillIdle ?? '', tone: 'idle' }
})

const peerStatus = computed<StatusPill>(() => {
  if (!props.presentationSupported) {
    return { icon: 'mdi:alert-circle-outline', label: ui.value?.pillUnsupported ?? '', tone: 'error' }
  }
  if (props.peerPresentersCount > 0) {
    const label = props.peerPresentersCount === 1
      ? ui.value?.pairingPeerConnectedOne ?? ''
      : (ui.value?.pairingPeerConnectedMany ?? '').replace('{count}', String(props.peerPresentersCount))
    return { icon: 'mdi:check-circle', label, tone: 'connected' }
  }
  if (!props.peerReady) {
    return { icon: 'mdi:progress-clock', label: ui.value?.pairingPeerConnecting ?? '', tone: 'connecting' }
  }
  return { icon: 'mdi:circle-outline', label: ui.value?.pairingPeerReady ?? '', tone: 'idle' }
})

// Brand palette is yellow/red/ink/chalk only, so Nuxt UI's `success` resolves
// to brand yellow and `warning` to brand red. For the pill semantics we want
// the standard green-for-good / amber-for-pending vocabulary, so the
// connected and connecting tones override with concrete Tailwind colors.
const pillToneClasses: Record<StatusTone, string> = {
  connected: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700',
  connecting: 'border-amber-500/40 bg-amber-500/10 text-amber-700',
  error: 'border-red-500/50 bg-red-500/10 text-red-700',
  idle: 'border-default bg-elevated/50 text-muted',
}
</script>

<template>
  <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
    <template #header>
      <div class="flex items-center gap-3">
        <UIcon
          class="text-primary size-9 shrink-0"
          name="mdi:presentation-play"
        />
        <div>
          <h2 class="text-lg font-semibold">
            {{ ui?.cardTitle }}
          </h2>
          <p class="text-sm text-muted">
            {{ ui?.cardDescription }}
          </p>
        </div>
      </div>
    </template>

    <UTabs
      :default-value="'this-device'"
      :items="presentationTabs"
      :ui="{ list: 'mb-4' }"
      variant="link"
    >
      <template #this-device>
        <div class="space-y-4">
          <p class="text-sm text-muted">
            {{ ui?.thisDeviceHint }}
          </p>
          <div class="flex items-center gap-2 flex-wrap">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium leading-none"
              :class="pillToneClasses[popupStatus.tone]"
            >
              <UIcon :name="popupStatus.icon" class="size-3.5"/>
              {{ popupStatus.label }}
            </span>
          </div>
          <UButton
            v-if="props.popupPresenterConnected"
            block
            color="warning"
            icon="mdi:close-box-multiple-outline"
            :label="ui?.disconnectButton"
            size="lg"
            variant="outline"
            @click="emit('disconnectPresentation')"
          />
          <UButton
            v-else
            block
            :disabled="!props.presentationSupported"
            icon="mdi:open-in-new"
            :label="ui?.connectButton"
            :loading="props.presentationConnecting"
            size="lg"
            @click="emit('connectPresentationWindow')"
          />
        </div>
      </template>

      <template #remote-display>
        <div class="space-y-4">
          <p class="text-sm text-muted">
            {{ ui?.pairingDescription }}
          </p>
          <div class="flex items-center gap-2 flex-wrap">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium leading-none"
              :class="pillToneClasses[peerStatus.tone]"
            >
              <UIcon :name="peerStatus.icon" class="size-3.5"/>
              {{ peerStatus.label }}
            </span>
            <UButton
              v-if="props.peerPresentersCount > 0"
              color="warning"
              icon="mdi:account-off-outline"
              :label="ui?.pairingDisconnectButton"
              size="xs"
              variant="outline"
              @click="emit('disconnectPeerPresenters')"
            />
          </div>
          <GamePresentationPairingCard
            v-if="props.presentationSupported"
            :pairing-code="props.pairingCode"
            :pairing-url="props.pairingUrl"
            :presenter-entry-url="props.presenterEntryUrl"
            @regenerate="emit('regeneratePairingCode')"
          />
        </div>
      </template>

      <template #use-as-display>
        <div class="space-y-4">
          <p class="text-sm text-muted">
            {{ ui?.useAsDisplayDescription }}
          </p>
          <UButton
            block
            icon="mdi:television-play"
            :label="ui?.launchDisplayModeButton"
            size="lg"
            @click="emit('launchDisplayMode')"
          />
        </div>
      </template>
    </UTabs>
  </UCard>
</template>
